import {useEffect, useState, useMemo} from 'react';
import {Layout} from '@/components/Layout';
import {useExternalContacts} from '@/contexts/ExternalContactsContext';
import {usePermissions} from '@/hooks/usePermissions';
import {Plus, Edit2, Trash2, Search, Mail, Phone, Building2, Box, UserPlus, Users, Check, Info, ShieldOff} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Textarea} from '@/components/ui/textarea';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {toast} from 'sonner';
import {api, getApiErrorMessage, resourceEndpoints} from '@/lib/api';
import {PERMISSIONS} from '@/lib/permissions';

const emptyForm = {name: '', nickname: '', phone: '', email: '', category: 'cliente', organization: '', notes: '', resourceIds: []};

const toLocalPhone = (value = '') => {
    const digits = String(value).replace(/\D/g, '');
    const withoutCountryCode = digits.length > 9 && digits.startsWith('351') ? digits.slice(3) : digits;
    return withoutCountryCode.slice(0, 9);
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const normalizeResource = (resource = {}) => {
    const attributes = resource.attributes && typeof resource.attributes === 'object' && !Array.isArray(resource.attributes)
        ? resource.attributes
        : {};
    const source = {...resource, ...attributes};
    const id = resource.id ?? source.id ?? source.resource_id ?? source.uuid;
    const objectType = source.object_type ?? source.type ?? {};
    const tag = source.tag ?? source.gest_tag ?? {};
    const reference = source.reference || source.code || id;
    const tagReference = tag.reference || tag.serial_no || tag.code || source.tag_reference || source.tag_code || source.tag_id || '';
    return {
        id: String(id || ''),
        label: source.name || source.title || reference || String(id || ''),
        reference: String(reference || id || ''),
        tagReference: String(tagReference || ''),
        kind: source.resource_type || source.type_name || objectType.name || objectType.title || source.category || 'recurso',
        status: source.status || '',
    };
};

export const ExternalContactsPage = () => {
    const {contacts, contactTypes, loading, error, addContact, updateContact, removeContact} = useExternalContacts();
    const {can} = usePermissions();

    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [resourceSearch, setResourceSearch] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [allResources, setAllResources] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(true);
    const [resourcesError, setResourcesError] = useState('');

    const canReadExternalContacts = can(PERMISSIONS.EXTERNAL_CONTACT_READ);
    const canCreateExternalContacts = can(PERMISSIONS.EXTERNAL_CONTACT_CREATE);
    const canUpdateExternalContacts = can(PERMISSIONS.EXTERNAL_CONTACT_UPDATE);
    const canDeleteExternalContacts = can(PERMISSIONS.EXTERNAL_CONTACT_DELETE);
    const canUseExternalContactActions = canUpdateExternalContacts || canDeleteExternalContacts;

    const filtered = useMemo(() => contacts.filter(c => {
        if (categoryFilter !== 'all' && c.category !== categoryFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            return [c.name, c.nickname, c.phone, c.email, c.organization].filter(Boolean).some(v => v.toLowerCase().includes(q));
        }
        return true;
    }), [contacts, search, categoryFilter]);

    const stats = useMemo(() => {
        const r = {total: contacts.length, withResources: 0};
        Object.keys(contactTypes).forEach(k => {
            r[k] = 0;
        });
        contacts.forEach(c => {
            r[c.category] = (r[c.category] || 0) + 1;
            if ((c.resourceIds || []).length > 0) r.withResources++;
        });
        return r;
    }, [contacts, contactTypes]);

    const openCreate = () => {
        if (!canCreateExternalContacts) {
            toast.error('Sem permissão para criar contactos.');
            return;
        }
        setEditingId(null);
        setForm({...emptyForm, category: Object.keys(contactTypes)[0] || emptyForm.category});
        setResourceSearch('');
        setDialogOpen(true);
    };
    const openEdit = (c) => {
        if (!canUpdateExternalContacts) {
            toast.error('Sem permissão para editar contactos.');
            return;
        }
        setEditingId(c.id);
        setForm({...emptyForm, ...c, phone: toLocalPhone(c.phone), resourceIds: c.resourceIds || []});
        setResourceSearch('');
        setDialogOpen(true);
    };

    const submit = async () => {
        if (editingId && !canUpdateExternalContacts) {
            toast.error('Sem permissão para editar contactos.');
            return;
        }
        if (!editingId && !canCreateExternalContacts) {
            toast.error('Sem permissão para criar contactos.');
            return;
        }
        if (!form.name.trim()) {
            toast.error('Nome é obrigatório.');
            return;
        }
        if (!form.phone.trim() && !form.email.trim()) {
            toast.error('Indique pelo menos telefone ou email.');
            return;
        }
        if (form.phone.trim() && !/^\d{9}$/.test(form.phone.trim())) {
            toast.error('O telefone deve ter 9 dígitos.');
            return;
        }
        const data = {
            name: form.name.trim(), nickname: form.nickname.trim(),
            phone: form.phone.trim(), email: form.email.trim(),
            category: form.category, organization: form.organization.trim(),
            notes: form.notes.trim(), resourceIds: [...(form.resourceIds || [])],
        };
        setSubmitting(true);
        try {
            if (editingId) {
                await updateContact(editingId, data);
                toast.success(`Contacto "${data.name}" atualizado.`);
            } else {
                await addContact(data);
                toast.success(`Contacto "${data.name}" adicionado.`);
            }
            setDialogOpen(false);
        } catch (requestError) {
            toast.error(requestError?.response?.data?.message || 'Não foi possível guardar o contacto.');
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (c) => {
        if (!canDeleteExternalContacts) {
            toast.error('Sem permissão para eliminar contactos.');
            return;
        }
        if (!window.confirm(`Eliminar contacto "${c.name}"?`)) return;
        setSubmitting(true);
        try {
            await removeContact(c.id);
            toast.success('Contacto removido.');
        } catch (requestError) {
            toast.error(requestError?.response?.data?.message || 'Não foi possível remover o contacto.');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleResource = (resourceId) => {
        setForm(prev => {
            const has = (prev.resourceIds || []).includes(resourceId);
            return {...prev, resourceIds: has ? prev.resourceIds.filter(x => x !== resourceId) : [...(prev.resourceIds || []), resourceId]};
        });
    };

    useEffect(() => {
        if (!canReadExternalContacts) {
            setAllResources([]);
            setResourcesLoading(false);
            return undefined;
        }

        let cancelled = false;

        const loadResources = async () => {
            setResourcesLoading(true);
            setResourcesError('');

            try {
                const response = await api.get(resourceEndpoints.list);
                const resources = collectionPayload(response.data, 'resources');
                const resourceItems = resources.length ? resources : collectionPayload(response.data, 'vehicles');
                const nextResources = resourceItems
                    .map(normalizeResource)
                    .filter(resource => resource.id);
                if (!cancelled) setAllResources(nextResources);
            } catch (requestError) {
                if (!cancelled) {
                    setAllResources([]);
                    setResourcesError(getApiErrorMessage(requestError, 'Nao foi possível carregar os recursos.'));
                }
            } finally {
                if (!cancelled) setResourcesLoading(false);
            }
        };

        loadResources();

        return () => {
            cancelled = true;
        };
    }, [canReadExternalContacts]);

    const resourcesForPicker = useMemo(() => {
        const q = resourceSearch.trim().toLowerCase();
        return allResources.filter(resource => !q || `${resource.id} ${resource.reference} ${resource.label} ${resource.tagReference}`.toLowerCase().includes(q)).slice(0, 30);
    }, [allResources, resourceSearch]);

    if (!canReadExternalContacts) {
        return (
            <Layout>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <Card className="p-8 text-center max-w-md">
                        <div className="w-14 h-14 rounded-2xl bg-red-100 mx-auto mb-4 flex items-center justify-center">
                            <ShieldOff className="text-red-600" size={28}/>
                        </div>
                        <h1 className="text-xl font-secondary font-bold text-slate-900">Acesso Negado</h1>
                        <p className="text-sm text-slate-600 mt-2">
                            Não tem permissão para consultar contactos.
                        </p>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-3xl font-secondary font-bold text-slate-900">Contactos</h1>
                        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                            Pessoas e organizações <strong>fora da equipa interna</strong> — clientes, fornecedores, parceiros — que podem ficar associadas a recursos para receberem notificações automáticas (ex: cliente avisado quando a sua entrega é confirmada via leitura da tag do recurso).
                        </p>
                        <p className="text-xs text-slate-500 mt-1 italic">Estes contactos não fazem login no sistema.</p>
                    </div>
                    {canCreateExternalContacts && (
                        <Button onClick={openCreate} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="ec-new-btn">
                            <Plus size={16} className="mr-2"/> Novo contacto
                        </Button>
                    )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Card className="p-4">
                        <div className="flex items-center justify-between"><p className="text-xs font-medium text-slate-600">Total</p><Users size={16} className="text-slate-500"/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{stats.total}</p>
                    </Card>
                    {Object.entries(contactTypes).map(([k, v]) => (
                        <Card key={k} className="p-4">
                            <div className="flex items-center justify-between"><p className="text-xs font-medium text-slate-600">{v.label}s</p>
                                <div className="w-3 h-3 rounded-full" style={{backgroundColor: v.color}}/>
                            </div>
                            <p className="text-2xl font-secondary font-bold" style={{color: v.color}}>{stats[k] || 0}</p>
                        </Card>
                    ))}
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[260px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                        <Input placeholder="Pesquisar nome, alcunha, telefone, email, organização..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} disabled={!canReadExternalContacts} data-testid="ec-search"/>
                    </div>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter} disabled={!canReadExternalContacts}>
                        <SelectTrigger className="w-[180px]" data-testid="ec-category-filter"><SelectValue/></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas as categorias</SelectItem>
                            {Object.entries(contactTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                {/* Lista */}
                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Contacto</TableHead>
                                <TableHead>Categoria</TableHead>
                                <TableHead>Telefone / Email</TableHead>
                                <TableHead>Organização</TableHead>
                                <TableHead>Recursos associados</TableHead>
                                {canUseExternalContactActions && <TableHead className="text-right">Ações</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(loading || error || filtered.length === 0) ? (
                                <TableRow><TableCell colSpan={canUseExternalContactActions ? 6 : 5} className="text-center text-sm text-slate-400 italic py-10">{loading ? 'A carregar contactos...' : error || 'Sem contactos para mostrar.'}</TableCell></TableRow>
                            ) : filtered.map(c => {
                                const cat = contactTypes[c.category] || contactTypes.outro || {label: c.category || 'Outro', color: '#64748b'};
                                return (
                                    <TableRow key={c.id} data-testid={`ec-row-${c.id}`}>
                                        <TableCell>
                                            <p className="font-medium text-slate-900">{c.name}{c.nickname && <span className="text-slate-400 text-xs ml-1">· {c.nickname}</span>}</p>
                                            {c.notes && <p className="text-[11px] text-slate-500 italic line-clamp-1 max-w-xs">{c.notes}</p>}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" style={{borderColor: cat.color, color: cat.color}}>{cat.label}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-0.5 text-xs">
                                                {c.phone && <span className="flex items-center gap-1 text-slate-700"><Phone size={10} className="text-slate-400"/>{c.phone}</span>}
                                                {c.email && <span className="flex items-center gap-1 text-slate-500"><Mail size={10} className="text-slate-400"/>{c.email}</span>}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-700">
                                            {c.organization || <span className="text-slate-400 italic">—</span>}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-[10px]">
                                                <Box size={10} className="mr-1"/>
                                                {(c.resourceIds || []).length} recurso{(c.resourceIds || []).length === 1 ? '' : 's'}
                                            </Badge>
                                        </TableCell>
                                        {canUseExternalContactActions && (
                                            <TableCell className="text-right">
                                                {canUpdateExternalContacts && (
                                                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(c)} disabled={submitting} data-testid={`ec-edit-${c.id}`}><Edit2 size={13} className="text-slate-600"/></Button>
                                                )}
                                                {canDeleteExternalContacts && (
                                                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => remove(c)} disabled={submitting} data-testid={`ec-delete-${c.id}`}><Trash2 size={13} className="text-red-500"/></Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>
            </div>

            {/* Dialog criar/editar */}
            <Dialog
                open={dialogOpen && (editingId ? canUpdateExternalContacts : canCreateExternalContacts)}
                onOpenChange={(open) => setDialogOpen(open && (editingId ? canUpdateExternalContacts : canCreateExternalContacts))}
            >
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ec-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {editingId ? <Edit2 size={18} className="text-blue-600"/> : <UserPlus size={18} className="text-blue-600"/>}
                            {editingId ? 'Editar contacto externo' : 'Novo contacto externo'}
                        </DialogTitle>
                        <DialogDescription>
                            Pessoas/organizações fora da equipa interna. Não fazem login. Podem receber alertas quando a tag de um dos recursos associados é lida.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Nome *</Label>
                                <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="ec-form-name"/>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Alcunha</Label>
                                <Input value={form.nickname} onChange={e => setForm(f => ({...f, nickname: e.target.value}))} placeholder="Opcional"/>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Telefone *</Label>
                                <div className="relative">
                                    <Phone size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: toLocalPhone(e.target.value)}))} placeholder="912345678" inputMode="numeric" maxLength={9} className="pl-7" data-testid="ec-form-phone"/>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Email *</Label>
                                <div className="relative">
                                    <Mail size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="email@..." className="pl-7" data-testid="ec-form-email"/>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Categoria *</Label>
                                <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
                                    <SelectTrigger data-testid="ec-form-category"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(contactTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Organização / Empresa</Label>
                                <div className="relative">
                                    <Building2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input value={form.organization} onChange={e => setForm(f => ({...f, organization: e.target.value}))} placeholder="Padaria do Bairro..." className="pl-7"/>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Notas</Label>
                            <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Horários preferidos, restrições, observações..."/>
                        </div>

                        {/* Recursos associados */}
                        <div className="border border-slate-200 rounded-lg bg-slate-50">
                            <div className="px-3 py-2 bg-white border-b border-slate-200 flex items-center justify-between">
                                <Label className="text-sm font-semibold flex items-center gap-2"><Box size={14} className="text-blue-600"/> Recursos associados</Label>
                                <Badge variant="outline" className="text-[10px]">{(form.resourceIds || []).length} selecionado{form.resourceIds?.length === 1 ? '' : 's'}</Badge>
                            </div>
                            <div className="px-3 py-2 text-[11px] flex items-start gap-2 bg-blue-50 text-blue-800 border-b border-blue-100">
                                <Info size={12} className="mt-0.5"/>
                                <span>Quando a tag de qualquer destes recursos for lida (ex: motorista entrega encomenda), o contacto recebe automaticamente uma notificação por email/SMS.</span>
                            </div>
                            <div className="p-2">
                                <div className="relative mb-2">
                                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
                                    <Input placeholder="Pesquisar recurso ou tag por ID ou referência..." className="pl-7 h-8 text-xs" value={resourceSearch} onChange={e => setResourceSearch(e.target.value)}/>
                                </div>
                                <div className="max-h-48 overflow-y-auto space-y-1">
                                    {(resourcesLoading || resourcesError || resourcesForPicker.length === 0) ? (
                                        <p className="text-xs text-slate-400 italic text-center py-3">{resourcesLoading ? 'A carregar recursos...' : resourcesError || 'Sem resultados.'}</p>
                                    ) : resourcesForPicker.map(t => {
                                        const checked = (form.resourceIds || []).includes(t.id);
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => toggleResource(t.id)}
                                                data-testid={`ec-resource-toggle-${t.id}`}
                                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded border transition-colors text-left ${checked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                                            >
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                                                    {checked && <Check size={10} className="text-white"/>}
                                                </div>
                                                <span className="font-mono text-[11px] text-slate-600">#{t.reference || t.id}</span>
                                                <span className="text-xs text-slate-700 flex-1 truncate">{t.label}</span>
                                                {t.tagReference && (
                                                    <Badge variant="outline" className="text-[9px] font-mono border-purple-200 text-purple-700 bg-purple-50">
                                                        Tag #{t.tagReference}
                                                    </Badge>
                                                )}
                                                <Badge variant="outline" className="text-[9px]">{t.kind}</Badge>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>Cancelar</Button>
                        <Button onClick={submit} disabled={submitting || (editingId ? !canUpdateExternalContacts : !canCreateExternalContacts)} className="bg-blue-600 hover:bg-blue-700" data-testid="ec-form-submit">
                            {submitting ? 'A guardar...' : editingId ? 'Guardar' : <><Plus size={14} className="mr-1"/>Adicionar</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};
